import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import MetricCard from './components/MetricCard';
import OnboardingFunnel from './components/OnboardingFunnel';
import ActivityHeatmap from './components/ActivityHeatmap';
import MerchantPerformanceTable from './components/MerchantPerformanceTable';
import FilterPanel from './components/FilterPanel';
import { merchantsService } from '../../services/supabaseService';

const MerchantAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [merchants, setMerchants] = useState([]);
  const [metricsData, setMetricsData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: merchantsData } = await merchantsService?.getAll(filters);
        const { data: stats } = await merchantsService?.getStatistics();

        if (merchantsData) {
          setMerchants(merchantsData);

          // Calculate metrics
          const totalMerchants = merchantsData?.length || 0;
          const activeMerchants = merchantsData?.filter(m => m?.status === 'active')?.length || 0;
          const avgSuccessRate = stats?.avgSuccessRate || 0;
          const avgChurnRisk = stats?.avgChurnRisk || 0;

          setMetricsData([
            {
              title: "Total Merchants",
              value: totalMerchants?.toString(),
              change: "+12.5%",
              changeType: "positive",
              icon: "Users",
              iconColor: "text-primary",
              iconBg: "bg-primary/10",
              description: "vs last month"
            },
            {
              title: "Onboarding Conversion",
              value: activeMerchants > 0 ? `${((activeMerchants / totalMerchants) * 100)?.toFixed(1)}%` : "0%",
              change: "+5.2%",
              changeType: "positive",
              icon: "TrendingUp",
              iconColor: "text-success",
              iconBg: "bg-success/10",
              description: "registration to activation"
            },
            {
              title: "Avg. Success Rate",
              value: `${avgSuccessRate?.toFixed(1)}%`,
              change: "+2.3%",
              changeType: "positive",
              icon: "Clock",
              iconColor: "text-warning",
              iconBg: "bg-warning/10",
              description: "transaction success"
            },
            {
              title: "Churn Risk Score",
              value: `${avgChurnRisk?.toFixed(1)}%`,
              change: "-3.4%",
              changeType: "positive",
              icon: "AlertTriangle",
              iconColor: "text-error",
              iconBg: "bg-error/10",
              description: "merchants at risk"
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching merchant data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  // Real-time subscription
  useEffect(() => {
    const unsubscribe = merchantsService?.subscribe((payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        setMerchants(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE' && payload?.new) {
        setMerchants(prev => prev?.map(m => m?.id === payload?.new?.id ? payload?.new : m));
      } else if (payload?.eventType === 'DELETE' && payload?.old) {
        setMerchants(prev => prev?.filter(m => m?.id !== payload?.old?.id));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const funnelData = [
    {
      stage: "Registration",
      description: "Initial merchant sign-up",
      count: merchants?.length || 0,
      icon: "UserPlus",
      color: "bg-primary"
    },
    {
      stage: "Document Submission",
      description: "Business verification documents",
      count: Math.floor((merchants?.length || 0) * 0.86),
      icon: "FileText",
      color: "bg-secondary"
    },
    {
      stage: "Verification",
      description: "KYC and business validation",
      count: Math.floor((merchants?.length || 0) * 0.75),
      icon: "CheckCircle",
      color: "bg-success"
    },
    {
      stage: "Activation",
      description: "First transaction completed",
      count: merchants?.filter(m => m?.status === 'active')?.length || 0,
      icon: "Zap",
      color: "bg-warning"
    }
  ];

  const heatmapData = [
    {
      cohort: "Jan 2026",
      totalMerchants: merchants?.filter(m => new Date(m?.registrationDate)?.getMonth() === 0)?.length || 0,
      activeRate: 82,
      avgTransactions: 156,
      periods: [
        { period: "Week 1", activity: 95 },
        { period: "Week 2", activity: 88 },
        { period: "Week 3", activity: 82 },
        { period: "Week 4", activity: 78 },
        { period: "Week 5", activity: 75 },
        { period: "Week 6", activity: 72 },
        { period: "Week 7", activity: 70 }
      ]
    }
  ];

  const merchantTableData = merchants?.map((merchant, index) => ({
    id: merchant?.id,
    name: merchant?.merchantName,
    category: merchant?.businessType || 'General',
    transactionVolume: merchant?.totalTransactions || 0,
    revenue: merchant?.totalRevenue || 0,
    revenueGrowth: 12.5,
    lastActivity: merchant?.updatedAt ? new Date(merchant.updatedAt)?.toLocaleDateString('id-ID') : '-',
    lastActivityTime: merchant?.updatedAt ? new Date(merchant.updatedAt)?.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) + ' WIB' : '-',
    status: merchant?.status || 'pending',
    riskLevel: merchant?.churnRiskScore > 50 ? 'high' : merchant?.churnRiskScore > 25 ? 'medium' : 'low'
  })) || [];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters applied:', newFilters);
  };

  const handleExport = () => {
    console.log('Exporting merchant analytics data...');
  };

  return (
    <>
      <Helmet>
        <title>Merchant Analytics - PaymentGateway Dashboard</title>
        <meta name="description" content="Track merchant onboarding progress, account performance, and engagement patterns across your merchant portfolio" />
      </Helmet>
      <Header />
      <main className="main-content bg-background">
        <div className="content-container">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              Merchant Analytics
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Track onboarding progress, account performance, and engagement patterns across your merchant portfolio
            </p>
          </div>

          <FilterPanel onFilterChange={handleFilterChange} onExport={handleExport} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {metricsData?.map((metric, index) => (
              <MetricCard key={index} {...metric} loading={loading} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-8">
              <OnboardingFunnel data={funnelData} loading={loading} />
            </div>
            <div className="lg:col-span-4">
              <ActivityHeatmap data={heatmapData} loading={loading} />
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <MerchantPerformanceTable data={merchantTableData} loading={loading} />
          </div>

          <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-xs md:text-sm text-muted-foreground">
                  Data refreshes every 15 minutes • Last updated: {new Date()?.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default MerchantAnalytics;